import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { getAhaCatalog } from "../../../api/backend";
import { CategoryPill } from "../../../components/knowledgeBank/catalog/KbCatalog";

const KIND_LABEL = { DEFAULT: "Certificate of completion", AHA: "AHA eCard" };
const DELIVERY_LABEL = { PHYSICAL: "In person", VIRTUAL: "Virtual", HYBRID: "Hybrid" };

const EYEBROW = { fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase" };
const MONO = { fontFamily: "var(--font-mono)" };
const hhmm = (t) => (t ? String(t).slice(0, 5) : "");

/** "In person", "Virtual", "Hybrid", or "Mixed" when the sessions differ; empty when there are none. */
export const deliveryOf = (sessions) => {
  const types = [...new Set(sessions.map((s) => s.sessionType).filter(Boolean))];
  if (types.length === 0) return "—";
  return types.length === 1 ? DELIVERY_LABEL[types[0]] || types[0] : "Mixed";
};

/** A session teaching AHA modules is named by them; otherwise it falls back to "Session N". */
const sessionTitle = (s, index, modules) => {
  const names = modules.filter((m) => (s.ahaModuleKeys || []).includes(m.key)).map((m) => m.name);
  return names.length ? names.join(", ") : `Session ${s.sessionNumber || index + 1}`;
};

function Fact({ label, children, first }) {
  return (
    <Box sx={{ pt: "14px", display: "grid", gap: "3px", pl: { xs: 0, sm: first ? 0 : "18px" }, borderLeft: { xs: 0, sm: first ? 0 : "1px solid var(--color-hairline-violet)" } }}>
      <Box component="span" sx={{ ...EYEBROW, fontSize: 10.5, letterSpacing: ".1em", color: "var(--color-on-dark-muted)" }}>{label}</Box>
      <Box component="b" sx={{ fontFamily: "var(--font-display)", fontSize: 19, fontWeight: 600 }}>{children}</Box>
    </Box>
  );
}

function ContentCard({ icon, label, lines }) {
  return (
    <Box sx={{ border: "1px solid var(--color-hairline-cloud)", borderRadius: "var(--radius-lg)", p: "14px", display: "grid", gap: 1, alignContent: "start" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, fontWeight: 600 }}>
        {icon}{label}
        <Box component="em" sx={{ ml: "auto", fontStyle: "normal", fontSize: 13, fontWeight: 600, ...MONO }}>{lines.length}</Box>
      </Box>
      {lines.length ? (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: "5px", fontSize: 13, lineHeight: 1.4 }}>
          {lines.map((line) => <Box component="li" key={line.key} sx={{ opacity: 0.8 }}>{line.text}</Box>)}
        </Box>
      ) : (
        <Box component="span" sx={{ fontSize: 12.5, opacity: 0.5 }}>None included</Box>
      )}
    </Box>
  );
}

function NoteBox({ icon, title, children }) {
  return (
    <Box sx={{ p: "12px 14px", borderRadius: "var(--radius-lg)", bgcolor: "var(--color-surface-press-light)", fontSize: 13, lineHeight: 1.5 }}>
      <Box component="b" sx={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", mb: "4px" }}>
        {icon}{title}
      </Box>
      {children}
    </Box>
  );
}

/** Read-only view of a standard course's published version. Nothing here can be edited. */
export default function StandardCoursePreview({ open, loading, summary, detail, onClose, onUse }) {
  const version = detail?.version;
  const productKey = version?.ahaProductKey;
  const [modules, setModules] = useState([]);
  useEffect(() => {
    if (!open || !productKey) { setModules([]); return undefined; }
    let live = true;
    getAhaCatalog()
      .then((c) => { if (live) setModules((c?.products || []).find((p) => p.key === productKey)?.modules || []); })
      .catch(() => { if (live) setModules([]); });
    return () => { live = false; };
  }, [open, productKey]);
  const sessions = detail?.sessions || [];
  const sheets = detail?.skillSheets || [];
  const tests = detail?.tests || [];
  const documents = detail?.documents || [];
  const ready = !loading && detail;
  const hours = version?.totalHours ?? summary?.totalHours;
  const issues = (version?.certificateKinds || []).map((k) => KIND_LABEL[k] || k);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="preview-standard-title"
      PaperProps={{ "data-testid": "standard-course-preview", sx: { borderRadius: "var(--radius-xxl)", overflow: "hidden", maxHeight: "88vh" } }}>
      <Box component="header" sx={{ position: "relative", px: { xs: "20px", sm: "28px" }, pt: "26px", pb: "22px", bgcolor: "var(--color-surface-night)", color: "var(--color-on-primary)" }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", pr: "44px" }}>
          {summary?.category && <Box sx={{ display: "inline-flex", bgcolor: "#fff", borderRadius: "var(--radius-sm)", p: "2px" }}><CategoryPill label={summary.category} /></Box>}
          {summary?.courseNumber && <Box component="span" sx={{ ...MONO, fontSize: 12, fontWeight: 500, color: "var(--color-accent-lime)" }}>{summary.courseNumber}</Box>}
          {version && <Box component="span" sx={{ fontSize: 12, color: "var(--color-on-dark-muted)" }}>Version {version.versionNumber}</Box>}
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px", ...EYEBROW, fontSize: 10.5, letterSpacing: ".1em", px: 1, py: "4px", borderRadius: "var(--radius-xs)", border: "1px solid var(--color-hairline-violet)", color: "var(--color-on-dark-muted)" }}>
            <LockOutlinedIcon sx={{ fontSize: 11 }} />Standard
          </Box>
        </Box>
        <Typography id="preview-standard-title" component="h2" sx={{ m: "12px 0 0", pr: "40px", fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 600, lineHeight: 1.15, letterSpacing: "-.01em" }}>
          {summary?.title}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close preview" size="small"
          sx={{ position: "absolute", top: 18, right: 18, width: 36, height: 36, borderRadius: "var(--radius-md)", border: "1px solid var(--color-hairline-violet)", color: "var(--color-on-dark-muted)", "&:hover": { color: "#fff", bgcolor: "var(--color-on-dark-faint)" } }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
        {ready && (
          <Box data-testid="standard-course-facts" sx={{ mt: "22px", borderTop: "1px solid var(--color-hairline-violet)", display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" } }}>
            <Fact label="Length" first>{hours ? <>{hours}<Box component="small" sx={{ fontSize: 12, fontWeight: 500, color: "var(--color-on-dark-muted)", ml: "3px" }}>{hours === 1 ? "hr" : "hrs"}</Box></> : "—"}</Fact>
            <Fact label="Sessions">{sessions.length}</Fact>
            <Fact label="Delivery">{deliveryOf(sessions)}</Fact>
            <Fact label="Issues">
              {issues.length === 0 ? "—" : issues.map((label) => (
                <Box component="span" key={label} sx={{ display: "block", fontSize: issues.length > 1 ? 14 : 19, lineHeight: 1.3 }}>{label}</Box>
              ))}
            </Fact>
          </Box>
        )}
      </Box>

      <DialogContent sx={{ px: { xs: "20px", sm: "28px" }, py: "24px", display: "grid", gap: "24px", alignContent: "start" }}>
        {!ready ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}><CircularProgress size={26} /></Box>
        ) : (
          <>
            {version.description && <Typography sx={{ fontSize: 15, lineHeight: 1.6 }}>{version.description}</Typography>}
            {version.prerequisites && (
              <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", gap: "12px", p: "14px 16px", borderRadius: "var(--radius-lg)", bgcolor: "var(--color-warn-bg)" }}>
                <WarningAmberOutlinedIcon sx={{ color: "var(--color-warn)", fontSize: 18, mt: "2px" }} />
                <Box>
                  <Box component="b" sx={{ display: "block", fontSize: 13, fontWeight: 600, mb: "2px" }}>Prerequisites</Box>
                  <Typography sx={{ fontSize: 14, lineHeight: 1.5 }}>{version.prerequisites}</Typography>
                </Box>
              </Box>
            )}

            <Box component="section" aria-label="Schedule">
              <Box sx={{ ...EYEBROW, opacity: 0.6, mb: "10px" }}>Schedule</Box>
              <Box sx={{ border: "1px solid var(--color-hairline-cloud)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
                {sessions.length === 0 && <Box sx={{ p: "12px 14px", fontSize: 13, opacity: 0.6 }}>No sessions</Box>}
                {sessions.map((s, i) => (
                  <Box key={s.id} sx={{ display: "grid", gridTemplateColumns: "28px minmax(0, 1fr) auto", gap: "12px", alignItems: "center", p: "12px 14px", borderTop: i ? "1px solid var(--color-hairline-cloud)" : 0 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: "var(--radius-sm)", bgcolor: "var(--color-surface-press-light)", display: "grid", placeItems: "center" }}>
                      <EventOutlinedIcon sx={{ fontSize: 14 }} />
                    </Box>
                    <Box>
                      <Box sx={{ fontSize: 14, fontWeight: 500 }}>{sessionTitle(s, i, modules)}</Box>
                      <Box sx={{ fontSize: 12.5, opacity: 0.6 }}>{DELIVERY_LABEL[s.sessionType] || s.sessionType}</Box>
                    </Box>
                    <Box component="span" sx={{ ...MONO, fontSize: 12.5, fontWeight: 500, opacity: 0.75, whiteSpace: "nowrap" }}>
                      {s.startTime && s.endTime ? `${hhmm(s.startTime)}–${hhmm(s.endTime)}` : ""}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box component="section" aria-label="Included content">
              <Box sx={{ ...EYEBROW, opacity: 0.6, mb: "10px" }}>Included content</Box>
              <Box sx={{ display: "grid", gap: "10px", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" } }}>
                <ContentCard icon={<FactCheckOutlinedIcon sx={{ fontSize: 14 }} />} label="Skill sheets"
                  lines={sheets.map((s) => ({ key: s.id, text: `${s.title}${s.requiredForPass ? " (required to pass)" : ""} · ${(s.items || []).length} steps` }))} />
                <ContentCard icon={<MonitorHeartOutlinedIcon sx={{ fontSize: 14 }} />} label="Tests"
                  lines={tests.map((t) => ({ key: t.id, text: `${t.title} · ${(t.questions || []).length} questions${t.passingScore ? ` · pass at ${t.passingScore}%` : ""}` }))} />
                <ContentCard icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 14 }} />} label="Documents"
                  lines={documents.map((d) => ({ key: d.id, text: `${d.title}${d.required ? " (required)" : ""}` }))} />
              </Box>
            </Box>

            <Box component="section" sx={{ display: "grid", gap: "10px", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" } }}>
              <NoteBox icon={<LockOutlinedIcon sx={{ fontSize: 13 }} />} title="Set by Training Center">
                Description, sessions, skill sheets, tests and documents.
              </NoteBox>
              <NoteBox icon={<EditOutlinedIcon sx={{ fontSize: 13 }} />} title="You choose">
                Dates, rooms, instructors and seat count when you schedule the class.
              </NoteBox>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: "20px", sm: "28px" }, py: "16px", borderTop: "1px solid var(--color-hairline-cloud)", gap: "10px", flexWrap: "wrap" }}>
        <Typography sx={{ fontSize: 12.5, opacity: 0.6, mr: "auto", width: { xs: "100%", sm: "auto" } }}>Creates a class you can schedule.</Typography>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onUse} disabled={!ready}>Use this course</Button>
      </DialogActions>
    </Dialog>
  );
}
